import {
  payment3_commonpb,
  Payment3_paymentmethod_coordinatorJSClient,
} from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import { AnyAction } from "@reduxjs/toolkit";
import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { PaymentComponentState } from "../../components";
import {
  StripeDefaultSetup,
  StripeSetupType,
} from "../../components/payment-processors/stripe-card-processor/components/stripe-elements/consts";
import { PaymentProcessorResponse } from "../../components/payment-processors/types";
import {
  AvailablePaymentMethods,
  defaultAvailablePaymentMethods,
  FormsSetupDefaultValue,
  PaymentMethods,
} from "../../const";
import {
  FormsSetupType,
  RequiredOwnerType,
  SetWalletCheckboxProps,
  StyleType,
  WalletCheckboxValue,
} from "../../types";
import {
  initialState,
  paymentSlice,
  PaymentState,
  setInternalContextState,
  setShowRadio,
} from "./state";
import { getFormsSetupDefaultValue } from "../../components/payment-processors/stripe-card-processor/components/stripe-elements/utils";
import {
  UpdateDefaultPaymentMethodRequest,
  UpdateDefaultPaymentMethodResponseType,
  useWalletService,
} from "./hooks";
import { PaymentMethod } from "../../models";
import { removePropertyRecursively } from "../../utils";
import Brand = payment3_commonpb.Brand;

export type PaymentContextValue = PaymentProcessorResponse &
  Pick<
    PaymentContextValueProps,
    | "formsSetup"
    | "availablePaymentMethods"
    | "token"
    | "tenant"
    | "paymentusIframeBaseUrl"
    | "applicationName"
    | "owner"
    | "hostJsClient"
    | "baseURL"
    | "googleAnalytics"
    | "testIdName"
    | "styleType"
    | "customerId"
    | "setWalletCheckboxProps"
    | "brand"
    | "subbrand"
  > & {
    state: PaymentState;
    dispatch: Dispatch<AnyAction>;
    updateDefaultPaymentMethod: (
      requestData: UpdateDefaultPaymentMethodRequest
    ) => Promise<UpdateDefaultPaymentMethodResponseType<PaymentMethod>>;
  };

export type PaymentContextValueProps = {
  // -------------------- Component State Related Props --------------------
  /**
   * When using this component in the un-controlled mode, this prop can be used
   * to set the initial state for this component.
   */
  initialState?: PaymentComponentState;

  /**
   * When using this component in the controlled mode, this prop can be used
   * to set the state for this component.
   */
  state?: PaymentComponentState;

  /**
   * When using this component in the controlled mode, this prop can be used
   * to attach an event handler to this component, that will be called whenever
   * the internal state is updated. The updated state object is passed as an
   * argument to the handler function.
   */
  onStateChange?: (state: PaymentComponentState | undefined) => void;
  /**
   * Application source for tracing
   */
  applicationName: string;
  /**
   * Owner -- now required for registration of ACH payments
   */
  owner?: RequiredOwnerType;
  /**
   *
   * * @deprecated
   * Payments input layout settings
   */
  stripeSetup?: StripeSetupType;
  /**
   * Forms setup for paymentus and stripe form
   */
  formsSetup?: FormsSetupType;
  /**
   * The tenant service to be used by as a header
   * default: ahs
   */
  tenant?: string;
  /**
   * Auth token passed to MFE via props
   */
  token?: string;
  /**
   * Default open payment method state
   */
  defaultOpenPaymentMethod?: PaymentMethods;
  /**
   * When only one payment method (credit card or bank account) needs to be displayed
   */
  availablePaymentMethods?: AvailablePaymentMethods;
  /**
   * The URL to be passed to Paymentus Iframe URL
   */
  paymentusIframeBaseUrl?: string;
  /**
   * Base URL for the payment micro service.
   */
  baseURL?: string;
  /**
   * The hostJsClient to be used for the PaymentsMethodMicroService, when it is passed in props by host .
   */
  hostJsClient?: Payment3_paymentmethod_coordinatorJSClient;
  /**
   * The googleAnalytics prop is passed to the Paymentus Iframe App and run tealium script for Google Analytics
   */
  googleAnalytics?: boolean;
  /**
   * Part of child components test IDs.
   */
  testIdName?: string;
  /**
   * Select one of two styles: for default style set "default" value or undefined, for AHS rebrand style set "AHSRebrand" value.
   */
  styleType?: StyleType;
  /**
   * This prop is intended for use only by the Customer Disbursement mfe, which imports and uses this common context.
   */
  customerId?: string;
  /**
   * SetWalletCheckboxProps is an object that contains all required properties to use the 'Set as default" payment method or "Save in Wallet" checkbox.
   */
  setWalletCheckboxProps?: SetWalletCheckboxProps;
  /**
   * Required brand identifier used for registration call and creating a new payment method.
   */
  brand: Exclude<Brand, Brand.HomeServicePlan | Brand.Unknown>;
  /**
   * subbrand is an optional identifier used to specify the brand when the main brand value is "HBW210"
   */
  subbrand?: payment3_commonpb.Subbrand;
};

export type PaymentContextProps = {
  value: PaymentContextValueProps & PaymentProcessorResponse;
  children: ReactNode;
  providerState?: PaymentContextValue;
};

export const PaymentContextInitialState: PaymentContextValue = {
  applicationName: "",
  owner: undefined,
  onSuccess: () => undefined,
  onFailure: () => undefined,
  onResponse: () => undefined,
  state: initialState,
  dispatch: () => undefined,
  formsSetup: FormsSetupDefaultValue,
  availablePaymentMethods: defaultAvailablePaymentMethods,
  token: "",
  paymentusIframeBaseUrl: "",
  baseURL: "",
  hostJsClient: undefined,
  googleAnalytics: false,
  styleType: "default",
  updateDefaultPaymentMethod: () => Promise.resolve(undefined),
  brand: Brand.Undefined_Brand,
};

const PaymentContext = createContext<PaymentContextValue>(
  PaymentContextInitialState
);

export const { Provider: PaymentContextProvider } = PaymentContext;

export const PaymentsContext: FC<PaymentContextProps> = ({
  value,
  children,
  providerState,
}) => {
  const {
    brand,
    subbrand,
    onSuccess,
    onFailure,
    onResponse,
    onStateChange,
    initialState: propsInitialState,
    state: propsState,
    stripeSetup,
    formsSetup = getFormsSetupDefaultValue(value.styleType),
    defaultOpenPaymentMethod,
    availablePaymentMethods,
    tenant,
    token,
    paymentusIframeBaseUrl,
    applicationName,
    owner,
    baseURL,
    hostJsClient,
    googleAnalytics,
    testIdName,
    styleType = "default",
    setWalletCheckboxProps,
  } = value;
  const [state, dispatch] = useReducer(paymentSlice.reducer, {
    ...initialState,
    internalState: propsInitialState || initialState.internalState,
    isSetAsDefaultChecked:
      setWalletCheckboxProps?.showWalletCheckbox ===
      WalletCheckboxValue.SET_AS_DEFAULT
        ? setWalletCheckboxProps?.defaultChecked
        : undefined,
    isSaveInWalletChecked:
      setWalletCheckboxProps?.showWalletCheckbox ===
      WalletCheckboxValue.SAVE_IN_WALLET
        ? setWalletCheckboxProps?.defaultChecked
        : undefined,
  });

  const formsSetupDefaultValue = getFormsSetupDefaultValue(styleType);

  const defaultSubbrand = useMemo(() => {
    return brand === payment3_commonpb.Brand.HBW210 && subbrand === undefined
      ? payment3_commonpb.Subbrand.HBW210Resale
      : subbrand;
  }, [brand, subbrand]);

  useEffect(() => {
    if (propsState) {
      state.internalState !== propsState &&
        dispatch(setInternalContextState(propsState));
    }
  }, [propsState, state]);

  useEffect(() => {
    onStateChange?.(state.internalState);
  }, [state.internalState, onStateChange]);

  useEffect(() => {
    if (defaultOpenPaymentMethod) {
      dispatch(
        setInternalContextState({
          ...{ activePaymentMethod: defaultOpenPaymentMethod },
        })
      );
    }
  }, [defaultOpenPaymentMethod]);

  useEffect(() => {
    if (availablePaymentMethods) {
      const availablePaymentMethodsArray = (Object.keys(
        availablePaymentMethods
      ) as Array<PaymentMethods>).filter((key) => availablePaymentMethods[key]);

      if (availablePaymentMethodsArray.length === 1) {
        dispatch(
          setInternalContextState({
            ...{ activePaymentMethod: availablePaymentMethodsArray[0] },
          })
        );
        dispatch(setShowRadio(false));
      }
    }
  }, [availablePaymentMethods]);

  const updateDefaultPaymentMethod = useWalletService(
    token,
    baseURL,
    setWalletCheckboxProps?.walletJsClientPassThrough,
    tenant
  );

  const formsSetupCombinedState: FormsSetupType = useMemo(
    () => ({
      ...formsSetup,
      stripeForm: {
        ...formsSetup?.stripeForm,
        fieldsSetup: {
          ...StripeDefaultSetup,
          ...formsSetup?.stripeForm?.fieldsSetup,
          ...stripeSetup,
        },
      },
      paymentusForm: {
        ...formsSetupDefaultValue?.paymentusForm,
        ...formsSetup?.paymentusForm,
        fieldsSetup: {
          ...formsSetupDefaultValue?.paymentusForm?.fieldsSetup,
          ...formsSetup?.paymentusForm?.fieldsSetup,
        },
      },
      braintreeForm: {
        ...formsSetupDefaultValue?.braintreeForm,
        ...formsSetup?.braintreeForm,
        fieldsSetup: {
          ...formsSetupDefaultValue?.braintreeForm?.fieldsSetup,
          ...formsSetup?.braintreeForm?.fieldsSetup,
        },
      },
    }),
    [
      stripeSetup,
      formsSetup,
      formsSetupDefaultValue?.braintreeForm,
      formsSetupDefaultValue?.paymentusForm,
    ]
  );

  if (formsSetupCombinedState?.stripeForm?.fieldsSetup?.postal_code) {
    formsSetupCombinedState.stripeForm.fieldsSetup.postal_code.required = true;
  }

  return (
    <PaymentContextProvider
      value={
        providerState
          ? providerState
          : {
              applicationName,
              brand,
              subbrand: defaultSubbrand,
              owner,
              state,
              dispatch,
              onSuccess,
              onFailure,
              onResponse,
              formsSetup: setWalletCheckboxProps?.showWalletCheckbox
                ? removePropertyRecursively<FormsSetupType>(
                    formsSetupCombinedState,
                    "saveInWallet"
                  )
                : formsSetupCombinedState,
              availablePaymentMethods,
              token,
              tenant,
              paymentusIframeBaseUrl,
              baseURL,
              hostJsClient,
              googleAnalytics,
              testIdName,
              styleType,
              setWalletCheckboxProps,
              updateDefaultPaymentMethod,
            }
      }
    >
      {children}
    </PaymentContextProvider>
  );
};

export const usePaymentContext = (): PaymentContextValue =>
  useContext<PaymentContextValue>(PaymentContext);
